import * as THREE from 'three/webgpu';
import type { Object3D } from 'three/webgpu';
import { isCadObjectRuntimeDerived } from '../../document';

import type {
  AssemblyMateTransparentMaterialInput, AssemblyMateRenderableObject,
  AssemblyMateTransparentMaterialReplacement, AssemblyMateFirstSelectionTransparencyInput,
  AssemblyMateFirstSelectionTransparencyControllerOptions,
} from '../../../types';
export type {
  AssemblyMateFirstSelectionTransparencyInput, AssemblyMateFirstSelectionTransparencyControllerOptions,
} from '../../../types';

/** 默认透明度，既能看见第一组件轮廓，也能暴露其后方组件。 */
const DEFAULT_FIRST_SELECTION_TRANSPARENCY_OPACITY = 0.35;

/** Mate 第一选择透明效果的实例投影 owner。 */
export const ASSEMBLY_MATE_FIRST_SELECTION_TRANSPARENCY_OWNER =
  'assembly-mate:first-selection-transparency';

/**
 * Mate PropertyManager“使第一个选择透明”的运行时控制器。
 *
 * 生命周期边界：
 * 1. 只处理当前配合命令会话中的临时显示效果；
 * 2. 不读取或写入 Mate Definition、文档模型、Vue 状态；
 * 3. 所有克隆材质都由本控制器释放，退出命令或切换第一个选择时恢复原材质。
 */
export class AssemblyMateFirstSelectionTransparencyController {
  private readonly opacity: number;
  private readonly temporaryAppearancePort: PuppyCAD.AssemblyComponentTemporaryAppearancePort | null;
  private readonly ownerToken: string;
  private readonly replacements = new Map<string, AssemblyMateTransparentMaterialReplacement>();
  private activeComponentInstanceId: string | null = null;
  private activeComponentObject: Object3D | null = null;

  /**
   * 构造函数。
   *
   * @param options 控制器选项。
   */
  public constructor(options: AssemblyMateFirstSelectionTransparencyControllerOptions = {}) {
    this.opacity = Number.isFinite(options.opacity)
      ? Math.min(1, Math.max(0, options.opacity ?? DEFAULT_FIRST_SELECTION_TRANSPARENCY_OPACITY))
      : DEFAULT_FIRST_SELECTION_TRANSPARENCY_OPACITY;
    this.temporaryAppearancePort = options.temporaryAppearancePort ?? null;
    this.ownerToken = options.ownerToken?.trim() || ASSEMBLY_MATE_FIRST_SELECTION_TRANSPARENCY_OWNER;
  }

  /**
   * 同步当前第一个选择所属组件的透明显示。
   *
   * @param input 透明显示目标；为空时恢复全部材质。
   * @returns 本次调用是否修改了材质显示状态。
   */
  public sync(input: AssemblyMateFirstSelectionTransparencyInput): boolean {
    if (!input.componentInstanceId || !input.componentObject) {
      return this.clear();
    }

    if (
      this.activeComponentInstanceId === input.componentInstanceId
      && this.activeComponentObject === input.componentObject
    ) {
      return false;
    }

    // 普通对象先恢复旧材质；实例对象只迁移临时显示 owner，不恢复 source 或重建 Unit。
    const cleared = this.restoreMaterialReplacements();
    this.activeComponentInstanceId = null;
    this.activeComponentObject = null;
    const instanceChanged = this.temporaryAppearancePort?.setTemporaryOpacity(
      this.ownerToken,
      input.componentObject,
      this.opacity,
    ) ?? false;
    const applied = this.applyToComponent(input.componentInstanceId, input.componentObject);
    this.activeComponentInstanceId = input.componentInstanceId;
    this.activeComponentObject = input.componentObject;
    return cleared || instanceChanged || applied;
  }

  /**
   * 清除当前透明显示并恢复原材质。
   *
   * @returns 是否恢复过材质。
   */
  public clear(): boolean {
    if (this.replacements.size === 0 && !this.activeComponentInstanceId) {
      return this.temporaryAppearancePort?.setTemporaryOpacity(this.ownerToken, null, this.opacity) ?? false;
    }

    // 普通材质与实例派生显示分别归还，不触碰正式实例矩阵和身份。
    const restored = this.restoreMaterialReplacements();
    this.activeComponentInstanceId = null;
    this.activeComponentObject = null;
    const instanceChanged = this.temporaryAppearancePort?.setTemporaryOpacity(
      this.ownerToken,
      null,
      this.opacity,
    ) ?? false;
    return restored || instanceChanged;
  }

  /**
   * 释放控制器持有的临时资源。
   */
  public dispose(): void {
    this.clear();
  }

  /**
   * 将组件树中的可渲染对象替换为透明材质副本。
   *
   * @param componentInstanceId 组件实例 ID。
   * @param componentObject 组件根对象。
   * @returns 是否至少替换了一个材质。
   */
  private applyToComponent(componentInstanceId: string, componentObject: Object3D): boolean {
    const visit = (object: Object3D): void => {
      // 整装配选取可能包含实例投影根；派生子树由各自 owner 管理，不能再克隆其材质。
      if (isCadObjectRuntimeDerived(object) || this.temporaryAppearancePort?.isInstancedSource(object)) return;
      this.applyToRenderableObject(object as AssemblyMateRenderableObject);
      object.children.forEach(visit);
    };
    visit(componentObject);

    if (this.replacements.size === 0) {
      return false;
    }

    this.activeComponentInstanceId = componentInstanceId;
    this.activeComponentObject = componentObject;
    return true;
  }

  /**
   * 恢复普通对象的透明材质，不修改实例渲染层的外观 owner。
   *
   * 该分离用于“第一个选择 A → B”切换：旧材质先恢复，owner 再直接迁移到 B，
   * 实例渲染层只同步两个范围的差异，不重新创建正式实例组。
   *
   * @returns 是否恢复过任意材质。
   */
  private restoreMaterialReplacements(): boolean {
    if (this.replacements.size === 0) {
      return false;
    }
    this.replacements.forEach((replacement) => {
      replacement.object.material = replacement.originalMaterial;
      this.disposeMaterialInput(replacement.transparentMaterial);
    });
    this.replacements.clear();
    return true;
  }

  /**
   * 替换单个渲染对象材质。
   *
   * @param object 可能带材质的场景对象。
   */
  private applyToRenderableObject(object: AssemblyMateRenderableObject): void {
    // 脱离 source 的材质仍供导出和其它实例借用，必须保持不变。
    if (this.temporaryAppearancePort?.isInstancedSource(object)) return;
    if (this.replacements.has(object.uuid)) {
      return;
    }

    const originalMaterial = this.readMaterialInput(object);
    if (!originalMaterial) {
      return;
    }

    const transparentMaterial = this.cloneTransparentMaterialInput(originalMaterial);
    if (!transparentMaterial) {
      return;
    }

    object.material = transparentMaterial;
    this.replacements.set(object.uuid, {
      object,
      originalMaterial,
      transparentMaterial,
    });
  }

  /**
   * 读取对象材质并过滤不支持的材质形态。
   *
   * @param object 可能带材质的对象。
   * @returns 可替换材质；不可替换时返回 null。
   */
  private readMaterialInput(object: AssemblyMateRenderableObject): AssemblyMateTransparentMaterialInput | null {
    const material = object.material;
    if (Array.isArray(material)) {
      return material.length > 0 && material.every((item) => this.isCloneableMaterial(item))
        ? material
        : null;
    }
    return this.isCloneableMaterial(material) ? material : null;
  }

  /**
   * 克隆材质并写入临时透明显示参数。
   *
   * @param material 原材质或材质数组。
   * @returns 透明材质副本。
   */
  private cloneTransparentMaterialInput(
    material: AssemblyMateTransparentMaterialInput,
  ): AssemblyMateTransparentMaterialInput | null {
    if (Array.isArray(material)) {
      return material.map((item) => this.cloneTransparentMaterial(item));
    }
    return this.cloneTransparentMaterial(material);
  }

  /**
   * 克隆单个材质。
   *
   * @param material 原材质。
   * @returns 透明材质副本。
   */
  private cloneTransparentMaterial(material: THREE.Material): THREE.Material {
    const transparentMaterial = material.clone();
    const sourceOpacity = typeof material.opacity === 'number' ? material.opacity : 1;

    /**
     * 使用 clone 而不是直接改原材质，是因为 imported glTF/STEP 组件常共享材质实例；
     * 直接修改会把同一零件的其它装配实例也错误透明化。
     */
    transparentMaterial.transparent = true;
    transparentMaterial.opacity = Math.min(sourceOpacity, this.opacity);
    transparentMaterial.depthWrite = false;
    transparentMaterial.needsUpdate = true;
    return transparentMaterial;
  }

  /**
   * 释放本控制器创建的材质副本。
   *
   * @param material 材质或材质数组。
   */
  private disposeMaterialInput(material: AssemblyMateTransparentMaterialInput): void {
    if (Array.isArray(material)) {
      material.forEach((item) => item.dispose());
      return;
    }
    material.dispose();
  }

  /**
   * 判断运行时对象是否是可克隆材质。
   *
   * @param value 待判断对象。
   * @returns 是否具备 three.js 材质所需的 clone/dispose 能力。
   */
  private isCloneableMaterial(value: unknown): value is THREE.Material {
    return Boolean(value)
      && typeof value === 'object'
      && typeof (value as { clone?: unknown }).clone === 'function'
      && typeof (value as { dispose?: unknown }).dispose === 'function';
  }
}
